/* Native mobile navigation and Shopify localization enhancements. */
(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  class FlatMobileMenu extends HTMLElement {
    connectedCallback() {
      this.attach();
      document.addEventListener('shopify:section:load', this.onLoad);
      document.addEventListener('shopify:section:unload', this.onUnload);
    }
    disconnectedCallback() {
      this.detach();
      document.removeEventListener('shopify:section:load', this.onLoad);
      document.removeEventListener('shopify:section:unload', this.onUnload);
    }
    onLoad = event => { if (event.target.contains(this)) this.attach(); };
    onUnload = event => { if (event.target.contains(this)) this.detach(); };
    attach() {
      if (this.attached) return;
      this.dialog = this.closest('dialog');
      this.host = this.closest('dialog-component');
      if (!this.dialog || !this.host) return;
      this.attached = true;
      this.header = this.closest('.shopify-section')?.querySelector('.header');
      this.dialog.addEventListener('click', this.onClick, true);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKey);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', this.onMotion);
      window.addEventListener('scroll', this.onScroll, { passive:true });
      window.addEventListener('resize', this.onResize);
      this.headerObserver = new MutationObserver(this.onScroll);
      if (this.header) this.headerObserver.observe(this.header, { attributes:true, attributeFilter:['data-sticky'] });
      this.onResize();
    }
    detach() {
      if (!this.attached) return;
      this.attached = false;
      this.closeNow();
      this.dialog.removeEventListener('click', this.onClick, true);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('keydown', this.onKey);
      this.motion.removeEventListener('change', this.onMotion);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.headerObserver.disconnect();
      cancelAnimationFrame(this.frame);
      this.header?.removeAttribute('data-mobile-shadow');
      this.header?.removeAttribute('data-mobile-shadow-ready');
    }
    onClick = event => {
      if (event.target.closest('dialog') !== this.dialog) return;
      if (event.target === this.dialog || event.target.closest('[data-dialog-close]')) {
        event.preventDefault(); event.stopPropagation(); this.requestClose();
      }
    };
    onCancel = event => {
      event.preventDefault();
      const localization = [...this.querySelectorAll('native-localization')].find(e => e.querySelector('details')?.open);
      if (localization) localization.close(true);
      else this.requestClose();
    };
    onKey = event => {
      if (event.key !== 'Tab' || !this.dialog.open) return;
      const controls = [...this.dialog.querySelectorAll('a[href],button,input,select,summary,[tabindex="0"]')]
        .filter(e => !e.disabled && !e.closest('[inert]') && e.getClientRects().length && getComputedStyle(e).visibility !== 'hidden');
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    onClose = () => {
      if (this.dialog.open) return;
      this.clearClosing();
      this.querySelectorAll('native-localization').forEach(e => e.close(false, true));
      const root = this.querySelector('[data-menu-root]');
      if (root) root.scrollTop = 0;
    };
    requestClose() {
      if (!this.dialog.open || this.closing) return;
      if (reduced()) { this.closeNow(); return; }
      const transform = getComputedStyle(this.dialog).transform;
      this.closing = true;
      this.dialog.classList.add('is-mobile-closing');
      if (this.dialog.animate) {
        this.animation = this.dialog.animate([{ transform }, { transform:'translateX(-100%)' }], { duration:500, easing:'cubic-bezier(.7,0,.2,1)', fill:'forwards' });
        this.animation.onfinish = () => this.closeNow();
      } else this.dialog.style.transform = 'translateX(-100%)';
      this.timer = setTimeout(() => this.closeNow(), 530);
    }
    clearClosing() {
      clearTimeout(this.timer);
      if (this.animation) { this.animation.onfinish = null; this.animation.cancel(); this.animation = null; }
      this.closing = false;
      this.dialog.classList.remove('is-mobile-closing');
      this.dialog.style.removeProperty('transform');
    }
    closeNow() {
      this.clearClosing();
      if (this.dialog.open) this.host.close();
    }
    onMotion = () => { if (this.motion.matches && this.closing) this.closeNow(); };
    onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (!this.header || !this.attached) return;
        if (this.header.hasAttribute('data-header-state-ready')) {
          this.header.removeAttribute('data-mobile-shadow-ready');
          this.header.removeAttribute('data-mobile-shadow');
          return;
        }
        const mobile = innerWidth < 1024;
        this.header.toggleAttribute('data-mobile-shadow-ready', mobile);
        this.header.toggleAttribute('data-mobile-shadow', mobile && this.header.dataset.sticky === 'true' && scrollY >= this.header.getBoundingClientRect().height);
      });
    };
    onResize = () => { if (innerWidth >= 1024 && this.dialog.open) this.closeNow(); this.onScroll(); };
  }
  class NativeLocalization extends HTMLElement {
    connectedCallback() {
      this.attach();
      document.addEventListener('shopify:section:load', this.onLoad);
      document.addEventListener('shopify:section:unload', this.onUnload);
    }
    disconnectedCallback() {
      this.detach();
      document.removeEventListener('shopify:section:load', this.onLoad);
      document.removeEventListener('shopify:section:unload', this.onUnload);
    }
    onLoad = event => { if (event.target.contains(this)) this.attach(); };
    onUnload = event => { if (event.target.contains(this)) this.detach(); };
    attach() {
      if (this.attached) return;
      this.details = this.querySelector('details');
      this.panel = this.querySelector('.native-localization__panel');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.panel) return;
      this.attached = true;
      this.search = this.querySelector('[data-country-search]');
      this.rows = [...this.querySelectorAll('[data-country-name]')];
      this.search?.parentElement.removeAttribute('hidden');
      this.dataset.ready = '';
      this.details.addEventListener('toggle', this.onToggle);
      this.summary.addEventListener('click', this.onSummary);
      this.addEventListener('input', this.filter);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('pointerdown', this.onOutside);
      window.addEventListener('resize', this.position);
      this.panel.inert = !this.details.open;
      this.sizeLanguage();
      document.fonts?.addEventListener('loadingdone', this.onFonts);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', this.onMotion);
    }
    detach() {
      if (!this.attached) return;
      this.attached = false;
      this.close(false, true);
      this.details.removeEventListener('toggle', this.onToggle);
      this.summary.removeEventListener('click', this.onSummary);
      this.removeEventListener('input', this.filter);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKey);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('pointerdown', this.onOutside);
      window.removeEventListener('resize', this.position);
      document.fonts?.removeEventListener('loadingdone', this.onFonts);
      this.motion.removeEventListener('change', this.onMotion);
      this.search?.parentElement.setAttribute('hidden', '');
      this.panel.inert = false;
      this.removeAttribute('data-ready');
    }
    clearAnimation() {
      clearTimeout(this.timer);
      if (this.animation) { this.animation.onfinish = null; this.animation.cancel(); this.animation = null; }
      this.closing = false;
    }
    onSummary = event => {
      if (this.closing) { event.preventDefault(); this.clearAnimation(); this.panel.inert = false; }
      else if (this.details.open) { event.preventDefault(); this.close(); }
    };
    onToggle = () => {
      if (!this.attached) return;
      this.panel.inert = !this.details.open;
      if (!this.details.open) { this.clearAnimation(); return; }
      if (this.closing) return;
      this.position();
      if (!reduced() && this.panel.animate) this.animation = this.panel.animate([{opacity:0,transform:'translateY(20px)'},{opacity:1,transform:'translateY(0)'}], {duration:350,easing:'cubic-bezier(.3,1,.3,1)'});
    };
    close(focus = false, instant = false) {
      if (!this.details?.open || this.closing && !instant) return;
      this.clearAnimation();
      if (focus && this.isConnected) this.summary.focus({preventScroll:true});
      const finish = () => { this.details.open = false; this.clearAnimation(); this.panel.inert = true; };
      if (instant || reduced() || !this.panel.animate) { finish(); return; }
      this.closing = true;
      this.panel.inert = true;
      this.animation = this.panel.animate([{opacity:1,transform:'translateY(0)'},{opacity:0,transform:'translateY(20px)'}], {duration:350,easing:'cubic-bezier(.3,1,.3,1)',fill:'forwards'});
      this.animation.onfinish = finish;
      this.timer = setTimeout(finish, 380);
    }
    filter = event => {
      if (event.target !== this.search) return;
      const query = this.search.value.trim().toLocaleLowerCase();
      this.rows.forEach(row => { row.hidden = !row.dataset.countryName.includes(query) && !row.dataset.countryCode.includes(query); });
      const empty = this.querySelector('[data-country-empty]');
      if (empty) empty.hidden = this.rows.some(row => !row.hidden);
    };
    onFonts = () => { if (this.attached) this.sizeLanguage(); };
    onMotion = () => { if (this.motion.matches) { if (this.closing) this.close(false, true); else this.clearAnimation(); } };
    sizeLanguage() {
      const select = this.querySelector('select[name="language_code"]');
      if (!select) return;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;
      context.font = getComputedStyle(select).font;
      select.style.width = `${Math.ceil(context.measureText(select.selectedOptions[0]?.textContent.trim() || '').width)+20}px`;
    }
    onChange = event => { if (event.target.matches('select[name="language_code"]')) { this.sizeLanguage(); const form = event.target.form; if (form.requestSubmit) form.requestSubmit(); else form.submit(); } };
    onKey = event => {
      if (event.key === 'Escape' && this.details.open) { event.preventDefault(); event.stopPropagation(); this.close(true); return; }
      if (!this.details.open || !['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
      if (event.target.matches('select')) return;
      const buttons = this.rows.filter(row => !row.hidden).map(row => row.querySelector('button'));
      if (!buttons.length) return;
      const index = buttons.indexOf(document.activeElement);
      if (event.target === this.search && !['ArrowDown','ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length-1 : event.key === 'ArrowDown' ? (index+1)%buttons.length : index < 0 ? buttons.length-1 : (index-1+buttons.length)%buttons.length;
      buttons[next].focus();
    };
    onFocusOut = event => { if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close(); };
    onOutside = event => { if (!this.contains(event.target)) this.close(); };
    position = () => {
      if (!this.details.open) return;
      this.style.removeProperty('--localization-shift');
      this.style.removeProperty('--localization-list-max');
      const trigger = this.summary.getBoundingClientRect(), panel = this.panel.getBoundingClientRect();
      const shift = Math.max(16 - trigger.left, Math.min(0, innerWidth - 16 - trigger.left - panel.width));
      this.style.setProperty('--localization-shift', `${shift}px`);
      this.style.setProperty('--localization-list-max', `${Math.max(38,Math.min(200,trigger.top-16-10-107))}px`);
    };
  }
  class ContactCountry extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-contact-country-fallback]');
      this.details = this.querySelector('[data-contact-country-details]');
      this.summary = this.details?.querySelector('summary');
      this.panel = this.querySelector('.contact-country__panel');
      this.search = this.querySelector('[data-contact-country-search]');
      this.input = this.querySelector('[data-contact-country-input]');
      this.value = this.querySelector('[data-contact-country-value]');
      this.rows = [...this.querySelectorAll('[data-contact-country-row]')];
      if (!this.select || !this.details || !this.summary || !this.panel || !this.input || !this.value) return;
      this.label = this.closest('.form-field')?.querySelector('label');
      this.originalLabelFor = this.label?.htmlFor;
      if (this.label) this.label.htmlFor = this.summary.id;
      this.select.disabled = true;
      this.details.hidden = false;
      this.input.disabled = false;
      this.dataset.ready = '';
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('keydown', this.onKey);
      this.details.addEventListener('toggle', this.onToggle);
      this.label?.addEventListener('click', this.onLabel);
      document.addEventListener('pointerdown', this.onOutside);
      this.closest('form')?.addEventListener('reset', this.onReset);
      this.panel.inert = !this.details.open;
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('keydown', this.onKey);
      this.details?.removeEventListener('toggle', this.onToggle);
      this.label?.removeEventListener('click', this.onLabel);
      document.removeEventListener('pointerdown', this.onOutside);
      this.closest('form')?.removeEventListener('reset', this.onReset);
      if (this.select) this.select.disabled = false;
      if (this.input) this.input.disabled = true;
      if (this.label) this.label.htmlFor = this.originalLabelFor || '';
    }
    onToggle = () => {
      this.panel.inert = !this.details.open;
      if (this.details.open) requestAnimationFrame(() => this.search?.focus({ preventScroll:true }));
      else this.clearSearch();
    };
    onLabel = event => {
      event.preventDefault();
      this.details.open = !this.details.open;
      if (!this.details.open) this.summary.focus({ preventScroll:true });
    };
    onClick = event => {
      const button = event.target.closest('[data-contact-country-row] button');
      if (!button) return;
      event.preventDefault();
      this.choose(button.value, button);
    };
    choose(country, activeButton) {
      this.input.value = country;
      this.value.textContent = country;
      this.rows.forEach(row => row.querySelector('button')?.toggleAttribute('aria-current', row.querySelector('button') === activeButton));
      this.details.open = false;
      this.summary.focus({ preventScroll:true });
    }
    onInput = event => {
      if (event.target !== this.search) return;
      const query = this.search.value.trim().toLocaleLowerCase();
      this.rows.forEach(row => { row.hidden = !row.dataset.countryName.includes(query) && !row.dataset.countryCode.includes(query); });
      const empty = this.querySelector('[data-contact-country-empty]');
      if (empty) empty.hidden = this.rows.some(row => !row.hidden);
    };
    clearSearch() {
      if (this.search) this.search.value = '';
      this.rows.forEach(row => { row.hidden = false; });
      const empty = this.querySelector('[data-contact-country-empty]');
      if (empty) empty.hidden = true;
    }
    onKey = event => {
      if (event.key === 'Escape' && this.details.open) {
        event.preventDefault();
        this.details.open = false;
        this.summary.focus({ preventScroll:true });
        return;
      }
      if (!this.details.open || !['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
      const buttons = this.rows.filter(row => !row.hidden).map(row => row.querySelector('button'));
      if (!buttons.length) return;
      const index = buttons.indexOf(document.activeElement);
      if (event.target === this.search && !['ArrowDown','ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length-1 : event.key === 'ArrowDown' ? (index+1)%buttons.length : index < 0 ? buttons.length-1 : (index-1+buttons.length)%buttons.length;
      buttons[next].focus();
    };
    onOutside = event => { if (this.details.open && !this.contains(event.target)) this.details.open = false; };
    onReset = () => requestAnimationFrame(() => {
      const option = this.select.selectedOptions[0];
      const button = this.rows.find(row => row.querySelector('button')?.value === option?.value)?.querySelector('button');
      if (option && button) this.choose(option.value, button);
    });
  }
  if (!customElements.get('flat-mobile-menu')) customElements.define('flat-mobile-menu', FlatMobileMenu);
  if (!customElements.get('native-localization')) customElements.define('native-localization', NativeLocalization);
  if (!customElements.get('contact-country')) customElements.define('contact-country', ContactCountry);
})();
